import { keepPreviousData, useQueries, useQuery, type QueryClient } from '@tanstack/react-query'
import type { ModelVariable, Place, WeatherReport } from '../shared/contract'
import { retainSections, retainHealthyForecast } from './data/progress'
import { markComplete, useBootGate, warmPaintPending } from './data/boot'
import { DataError, source, type NearPlace } from './data/source'
import { localDate } from '../shared/units'
import { inLower48Radar } from './lib/radarCoverage'
import { presets } from './lib/timemachine'
import { IS_MOCK, MOCK_SCENARIO } from './mockMode'
import { selectedPlace, useApp, type SavedPlace } from './store'

/**
 * React Query over the DataSource (engine in the browser, or labelled demo data).
 * Weather refetches every 5 min while the page is visible; previous data stays on screen
 * during refetches and after a failed refresh (the UI shows its age). On open, the last
 * saved reports are painted first (data/boot.ts); a cold build streams a first-screen
 * report internally; only the settled result enters the UI cache.
 */
export const FIVE_MIN = 5 * 60_000
type LatLon = Pick<Place, 'latitude' | 'longitude'>

const latLonKey = (p: LatLon) => `${p.latitude.toFixed(3)},${p.longitude.toFixed(3)}`

/** A source that missed its deadline says so in its status detail ("Timed out after …"). */
export function hasTimedOutSources(report: WeatherReport): boolean {
  return report.sources.some((s) => s.status === 'unavailable' && /timed out/i.test(s.detail))
}

/**
 * The engine serves a report for up to 5 minutes and rebuilds older ones in the background,
 * so a 5-minute refetch (or reopening the app) can receive the previous build. When what
 * arrived is older than this, ask for the rebuild and show it the moment it lands.
 */
const STALE_REPORT_MS = 4 * 60_000
function refreshIfStale(client: QueryClient, queryKey: readonly unknown[], place: LatLon, report: WeatherReport) {
  if (!source.refresh || Date.now() - Date.parse(report.generated_at) < STALE_REPORT_MS) return
  source
    .refresh(place.latitude, place.longitude)
    .then((fresh) => {
      const shown = client.getQueryData<WeatherReport>(queryKey)
      if (!shown || Date.parse(fresh.generated_at) >= Date.parse(shown.generated_at)) client.setQueryData(queryKey, retainHealthyForecast(shown, retainSections(shown, fresh)))
    })
    .catch(() => {
      /* the next refetch tries again */
    })
}

const lateRefreshAt = new Map<string, number>()
const LATE_REFRESH_DELAY_MS = 10_000

/**
 * Slow sources keep downloading after their deadline and warm the engine cache, so one quick
 * rebuild ~10 s later fills them in (tides, nowcast, normals) instead of waiting for the
 * 5-minute refresh. At most once per place per 5 minutes.
 */
function scheduleLateRefresh(client: QueryClient, queryKey: readonly unknown[], place: LatLon, report: WeatherReport) {
  if (!source.refresh || !hasTimedOutSources(report)) return
  const k = latLonKey(place)
  const last = lateRefreshAt.get(k) ?? 0
  if (Date.now() - last < FIVE_MIN) return
  lateRefreshAt.set(k, Date.now())
  setTimeout(() => {
    source
      .refresh?.(place.latitude, place.longitude)
      .then((fresh) => {
        const shown = client.getQueryData<WeatherReport>(queryKey)
        if (!shown || Date.parse(fresh.generated_at) >= Date.parse(shown.generated_at)) client.setQueryData(queryKey, retainHealthyForecast(shown, retainSections(shown, fresh)))
      })
      .catch(() => {
        /* the regular refresh will try again */
      })
  }, LATE_REFRESH_DELAY_MS)
}

export function weatherQueryOptions(place: LatLon) {
  const queryKey = ['weather', place.latitude.toFixed(3), place.longitude.toFixed(3), IS_MOCK ? MOCK_SCENARIO : 'live'] as const
  return {
    queryKey,
    // Demo reports are resident code/data, so an offline browser must not pause their boot promise.
    networkMode: IS_MOCK ? ('always' as const) : ('online' as const),
    queryFn: ({ client }: { client: QueryClient }) => {
      let progress: WeatherReport | undefined
      return source
        // The skeleton (or timestamped saved generation) stays until all source deadlines settle.
        .report(place.latitude, place.longitude, early => { progress = retainSections(progress, early) })
        .then((report) => {
          const previous = client.getQueryData<WeatherReport>(queryKey)
          markComplete(previous)
          markComplete(report)
          scheduleLateRefresh(client, queryKey, place, report)
          report = retainHealthyForecast(progress, retainSections(previous, retainSections(progress, report)))
          report = retainHealthyForecast(previous, report)
          refreshIfStale(client, queryKey, place, report)
          return report
        })
    },
    // Just under the engine's 5-minute freshness window: refocusing the tab (or switching back
    // to a place) within it costs no worker round trip; the 5-minute interval still refreshes.
    staleTime: IS_MOCK ? Infinity : (query: { state: { data: unknown } }) => (query.state.data as WeatherReport | undefined)?.averages ? 4 * 60_000 : 0,
    gcTime: 30 * 60_000,
    refetchInterval: IS_MOCK ? (false as const) : FIVE_MIN,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: !IS_MOCK,
    placeholderData: keepPreviousData,
    retry: (count: number, err: unknown) => {
      if (err instanceof DataError && err.kind === 'coverage') return false
      return count < 2
    },
  }
}

/** At start-up the selected place fetches first; the others wait for it (data/boot.ts). */
function useFetchAllowed(): (p: LatLon) => boolean {
  const gate = useBootGate()
  const selected = useApp((s) => latLonKey(selectedPlace(s)))
  return (p) => gate || (!warmPaintPending() && latLonKey(p) === selected)
}

export function useWeather(place: LatLon) {
  const allowed = useFetchAllowed()
  return useQuery({ ...weatherQueryOptions(place), enabled: allowed(place) })
}

/** Independent observers let a tile subscribe only to the inputs it consumes. */
export function useWeatherSection<T>(place: LatLon, select: (report: WeatherReport) => T) {
  const allowed = useFetchAllowed()
  return useQuery({ ...weatherQueryOptions(place), enabled: allowed(place), select })
}

/** Reports for every place in the list (sidebar mini cards, phone pages, map pins). */
export function useWeatherMany(places: LatLon[]) {
  const allowed = useFetchAllowed()
  return useQueries({
    queries: places.map((p) => ({ ...weatherQueryOptions(p), placeholderData: undefined, enabled: allowed(p) })),
  })
}

export function usePlaceSearch(query: string) {
  const term = query.trim()
  return useQuery({
    queryKey: ['places', term.toLowerCase(), IS_MOCK],
    networkMode: IS_MOCK ? 'always' : 'online',
    queryFn: () => source.places(term),
    enabled: term.length >= 2,
    staleTime: 10 * 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

export function reverseGeocode(latitude: number, longitude: number): Promise<NearPlace> {
  return source.reverse(latitude, longitude)
}

export function useModels(place: SavedPlace, variable: ModelVariable, enabled: boolean) {
  return useQuery({
    queryKey: ['models', place.latitude.toFixed(3), place.longitude.toFixed(3), variable, IS_MOCK],
    queryFn: () => source.models(place.latitude, place.longitude, variable),
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    retry: 1,
  })
}

/** Time Machine history for a range. Past data barely changes: long stale time, no polling. */
function historyOptions(place: LatLon, start: string, end: string) {
  return {
    queryKey: ['history', place.latitude.toFixed(3), place.longitude.toFixed(3), start, end, IS_MOCK] as const,
    queryFn: () => source.history(place.latitude, place.longitude, start, end),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  }
}

export function useHistory(place: LatLon, start: string, end: string, enabled = true) {
  return useQuery({
    ...historyOptions(place, start, end),
    enabled,
    placeholderData: keepPreviousData,
    retry: (count: number, err: unknown) => !(err instanceof DataError && err.kind === 'coverage') && !(err instanceof Error && /range|dates|before today/i.test(err.message)) && count < 1,
  })
}

/**
 * Fetch the range Past weather opens on ("Last 30 days") ahead of the first open, so the sheet draws
 * the record instead of a skeleton while a station is found. Called once at idle, after the chunk is
 * warm; needs the place's report (its time zone decides "today"), so it does nothing without one.
 */
export function prefetchPastWeather(client: QueryClient, place: SavedPlace): void {
  const report = client.getQueryData<WeatherReport>(weatherQueryOptions(place).queryKey)
  if (!report) return
  const last30 = presets(localDate(new Date().toISOString(), report.location.timezone))[1]
  void client.prefetchQuery({ ...historyOptions(place, last30.start, last30.end), retry: false })
}

/** Days 11–16 + CPC outlooks (updated a few times a day upstream). */
export function useOutlook(place: LatLon, enabled = true) {
  return useQuery({
    queryKey: ['outlook', place.latitude.toFixed(3), place.longitude.toFixed(3), IS_MOCK],
    queryFn: () => source.outlook(place.latitude, place.longitude),
    enabled,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: 1,
  })
}

export function useRadar(enabled = true, place?: { latitude: number; longitude: number } | null) {
  // One shared loop for the lower 48; a location-specific mosaic elsewhere.
  const region = !place || inLower48Radar(place.latitude, place.longitude) ? 'us' : `${place.latitude.toFixed(1)},${place.longitude.toFixed(1)}`
  return useQuery({
    queryKey: ['radar', source.kind, region],
    queryFn: () => (region === 'us' ? source.radar() : source.radar(place!.latitude, place!.longitude)),
    enabled,
    staleTime: 4 * 60_000,
    refetchInterval: FIVE_MIN,
    refetchIntervalInBackground: false,
  })
}
